import { createContext, useCallback, useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { useStoreContext } from 'features/store';
import { useGetStoreFn } from 'features/store/hooks';
import { IRbiRestaurant } from 'features/store/types';
import {
  getCurrentDateInStoreTimeZone,
  getRestaurantLatestClosingTime,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/menu/main-menu';
import { DAY_PART_SELECTIONS } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { isPickup } from 'utils/service-mode/service-mode';

import { ISmartAlertsContext, SmartAlert } from './types';

const MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS = 30;
const MINUTES_BEFORE_RESTAURANT_CLOSE = 30;
const MINUTES_BEFORE_PICK_UP_WINDOW_ENDS = 30;
const SMART_ALERT_REFRESH_INTERVAL = 60 * 1000;

export const SmartAlertsContext = createContext<ISmartAlertsContext>({} as ISmartAlertsContext);

export const useSmartAlert = (): ISmartAlertsContext => {
  const isSmartAlertsEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMART_ALERTS);
  const { formatMessage } = useIntl();
  const [tsmSmartAlert, setTsmSmartAlert] = useState<SmartAlert | null>(null);
  const [orderSmartAlert, setOrderSmartAlert] = useState<SmartAlert | null>(null);
  const [orderRestaurant, setOrderRestaurant] = useState<IRbiRestaurant | null>(null);
  const [serverOrderServiceMode, setServerOrderServiceMode] = useState<ServiceMode | null>(null);
  const [serverOrderStoreId, setServerOrderStoreId] = useState<string | null>(null);

  const { store: restaurant } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();

  const getRestaurant = useGetStoreFn();

  const fetchOrderRestaurant = useCallback(
    async (serverOrderStoreId: string) => {
      try {
        const orderRestaurant = await getRestaurant(serverOrderStoreId);
        setOrderRestaurant(orderRestaurant || null);
      } catch (error) {
        logger.error({ error, message: 'Failed to fetch server order restaurant' });
        setOrderRestaurant(null);
      }
    },
    [getRestaurant]
  );

  const breakfastInterval = activeDayParts.find(
    item => item?.key === DAY_PART_SELECTIONS.BREAKFAST_KEY
  );

  const generateOrderAlert = useCallback(() => {
    // Pick Up Window:
    // Check if the order is for a pickup service mode and the restaurant is about to close.
    // The "pickup window" refers to the time period during which a customer is allowed to pick up their order.
    // If the current time is within MINUTES_BEFORE_PICK_UP_WINDOW_ENDS minutes before the latest pickup closing time, show an alert.
    const isPickupServiceModeOrder = serverOrderServiceMode && isPickup(serverOrderServiceMode);
    if (isPickupServiceModeOrder && orderRestaurant) {
      const restaurantCurrentTime = getCurrentDateInStoreTimeZone(orderRestaurant as IRestaurant);
      const { closingTime } =
        getRestaurantLatestClosingTime({
          // For the Order alert, we must use the restaurant where the order was placed, not the currently selected restaurant
          restaurant: orderRestaurant as IRestaurant,
          onlyPickUpServiceModes: true,
        }) || {};

      if (closingTime) {
        const minutesForClosing = differenceInMinutes(closingTime, restaurantCurrentTime);

        if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_PICK_UP_WINDOW_ENDS) {
          setOrderSmartAlert({
            message: formatMessage({ id: 'pickupWindow' }, { minutes: minutesForClosing }),
          });

          return;
        }
      }
    }

    setOrderSmartAlert(null);
  }, [formatMessage, orderRestaurant, serverOrderServiceMode]);

  const generateTsmAlert = useCallback(() => {
    if (!restaurant) {
      setTsmSmartAlert(null);
      return;
    }

    const restaurantCurrentTime = getCurrentDateInStoreTimeZone(restaurant);

    const remainingMinutesToBreakfastDayPartChange = breakfastInterval?.endTime
      ? differenceInMinutes(breakfastInterval?.endTime, restaurantCurrentTime)
      : 0;

    // Breakfast DayPart ends
    if (
      remainingMinutesToBreakfastDayPartChange > 0 &&
      remainingMinutesToBreakfastDayPartChange <= MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS
    ) {
      setTsmSmartAlert({
        message: formatMessage(
          { id: 'breakfastDayPartEnds' },
          { minutes: remainingMinutesToBreakfastDayPartChange }
        ),
        linkUrl: routes.storeLocator,
      });

      return;
    }

    // Restaurant closing time
    const { closingTime } =
      getRestaurantLatestClosingTime({
        restaurant,
        onlyPickUpServiceModes: true,
      }) || {};

    if (closingTime) {
      const minutesForClosing = differenceInMinutes(closingTime, restaurantCurrentTime);

      if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_RESTAURANT_CLOSE) {
        setTsmSmartAlert({
          message: formatMessage({ id: 'restaurantCloses' }, { minutes: minutesForClosing }),
          linkUrl: routes.storeLocator,
        });

        return;
      }
    }
    setTsmSmartAlert(null);
  }, [breakfastInterval?.endTime, formatMessage, restaurant]);

  const generateAlerts = useCallback(() => {
    generateOrderAlert();
    generateTsmAlert();
  }, [generateOrderAlert, generateTsmAlert]);

  useEffect(() => {
    if (!isSmartAlertsEnabled) {
      return;
    }

    const interval = setInterval(() => {
      generateAlerts();
    }, SMART_ALERT_REFRESH_INTERVAL);

    generateAlerts();

    return () => {
      clearInterval(interval);
    };
  }, [generateAlerts, isSmartAlertsEnabled]);

  useEffect(() => {
    if (!isSmartAlertsEnabled || !serverOrderStoreId) {
      return;
    }

    fetchOrderRestaurant(serverOrderStoreId);
  }, [fetchOrderRestaurant, isSmartAlertsEnabled, serverOrderStoreId]);

  return {
    tsmSmartAlert,
    orderSmartAlert,
    setServerOrderServiceMode,
    setServerOrderStoreId,
  };
};

export const SmartAlertsProvider = ({ children }: IBaseProps) => {
  const ctxValue = useSmartAlert();
  return <SmartAlertsContext.Provider value={ctxValue}>{children}</SmartAlertsContext.Provider>;
};
